import { useEffect, useState } from 'react';

// HOW BIG A PICTURE ACTUALLY IS, in its own pixels, by URL.
//
// Everything in this app addresses a photograph in fractions of itself — a box is four
// numbers between 0 and 1 — which is what makes the same rectangle land correctly on a
// 90-pixel tile and on a full-screen zoom. The one question fractions cannot answer is how
// many PIXELS a fraction is, and that is the question a scale is: millimetres per pixel.
//
// It is also the question that catches the trap. One unit of x is the picture's whole WIDTH
// and one unit of y its whole HEIGHT, so on a 1000x1400 photograph the two axes are 1.4
// apart before any lens is involved. Anything comparing the axes has to come through here
// first.
//
// Module scope and never cleared: it is a property of the file, the same for every part
// found in that photograph, and a listing is eight numbers. It survives an unmount, which is
// what stops a re-open from re-measuring pictures the browser already has.
const sizes = new Map<string, { w: number; h: number }>();

export function useImageSizes(urls: string[]): Map<string, { w: number; h: number }> {
  const [, bump] = useState(0);
  useEffect(() => {
    let alive = true;
    const missing = urls.filter((u) => u && !sizes.has(u));
    if (!missing.length) return;
    for (const url of missing) {
      const img = new Image();
      img.onload = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;
        sizes.set(url, { w: img.naturalWidth, h: img.naturalHeight });
        // React coalesces these: eight pictures off the same cache land in one render, and
        // the ones that arrive later re-measure only their own tile.
        if (alive) bump((n) => n + 1);
      };
      img.src = url;
    }
    return () => { alive = false; };
  }, [urls.join('|')]);  // eslint-disable-line react-hooks/exhaustive-deps
  return sizes;
}

// THE SAME MEASUREMENT, AWAITED — for a run rather than a render.
//
// A hook cannot be called from a button's handler, and what a run needs is not "tell me when
// you know" but "hold until you do". Same cache, so a picture measured for a tile is not
// measured again here, and a URL that will not load resolves rather than hanging the press.
export async function measureImages(urls: string[]): Promise<Map<string, { w: number; h: number }>> {
  await Promise.all(urls.filter((u) => u && !sizes.has(u)).map((url) => new Promise<void>((done) => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        sizes.set(url, { w: img.naturalWidth, h: img.naturalHeight });
      }
      done();
    };
    img.onerror = () => done();
    img.src = url;
  })));
  return sizes;
}
